import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Button,
  Spinner,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { getFormErrors } from 'http-client/apiClient';
import { useCheckIdentifierMutation, useLoginMutation } from 'http-client/hooks/authHooks';
import {
  ApiResponse,
  CheckIdentifierRequestDto,
  LoginDto,
  TenantSelectDto,
  LoginResponseDto,
} from 'http-client/types/auth.types';

const TenantLogin: React.FC = () => {
  const navigate = useNavigate();

  // Step 1: Check Identifier Hook
  const {
    mutate: checkIdentifier,
    isPending: isCheckingIdentifier,
    error: checkError,
  } = useCheckIdentifierMutation();

  // Step 3: Login Hook (with optional tenantIdentifier via X-Tenant-ID header)
  const { mutate: login, isPending: isLoggingIn, error: loginError } = useLoginMutation();

  // Local State Management
  const [step, setStep] = useState<'identifier' | 'tenant-select' | 'password'>('identifier');
  const [availableTenants, setAvailableTenants] = useState<TenantSelectDto[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [identifier, setIdentifier] = useState<string>('');

  // Step 1: Form for Email/Phone
  const identifierForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      emailOrPhone: '',
    } as CheckIdentifierRequestDto,
    validationSchema: Yup.object({
      emailOrPhone: Yup.string().required('Please enter a valid email or phone number'),
    }),
    onSubmit: (values) => {
      checkIdentifier(values, {
        onSuccess: (response: ApiResponse<TenantSelectDto[]>) => {
          const tenants = response?.data || [];
          setIdentifier(values.emailOrPhone);
          setAvailableTenants(tenants);

          if (tenants.length === 1) {
            // Single tenant, go directly to password
            localStorage.setItem('tenantId', tenants[0].tenantId);
            setSelectedTenant(tenants[0].tenantId);
            setStep('password');
          } else if (tenants.length > 1) {
            // Multiple tenants, show selection
            setStep('tenant-select');
          }
        },
      });
    },
  });

  // Step 3: Form for Password (with selected tenant)
  const passwordForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      emailOrPhone: identifier,
      password: '',
    } as LoginDto,
    validationSchema: Yup.object({
      password: Yup.string()
        .required('Password is required')
        .min(6, 'Password must be at least 6 characters'),
    }),
    onSubmit: (values: LoginDto) => {
      login(values, {
        onSuccess: () => navigate('/dashboard'),
        onError: (err) => console.error(err), // optional
      });
    },
  });

  // Handle Tenant Selection
  const handleTenantSelect = (tenantIdentifier: string) => {
    setSelectedTenant(tenantIdentifier);
    setStep('password');
  };

  // Handle Back Button
  const handleBack = () => {
    if (step === 'password' && availableTenants.length > 1) {
      setStep('tenant-select');
    } else if (step === 'tenant-select' || step === 'password') {
      setIdentifier('');
      setAvailableTenants([]);
      setSelectedTenant('');
      setStep('identifier');
      identifierForm.resetForm();
    }
  };

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const response = await fetch('/cats'); // Adjust endpoint if needed
        if (!response.ok) throw new Error('Failed to fetch cats');
        const data = await response.json();
        console.log(data);
      } catch (error) {
        console.error('Error fetching cats:', error);
      }
    };

    fetchCats();
  }, []);

  return (
    <React.Fragment>
      <div
        className="min-vh-100 d-flex align-items-center justify-content-center py-5"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              {/* Main Card */}
              <Card
                className="border-0 shadow-lg overflow-hidden"
                style={{ borderRadius: '1.5rem' }}
              >
                {/* Animated gradient header */}
                <div
                  className="position-relative"
                  style={{
                    background: 'linear-gradient(120deg, #4F46E5 0%, #7C3AED 100%)',
                    padding: '2rem 1.5rem',
                    textAlign: 'center',
                  }}
                >
                  <div className="mb-3">
                    <div className="bg-white bg-opacity-20 rounded-circle d-inline-flex p-3">
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 2L2 7L12 12L22 7L12 2Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2 17L12 22L22 17"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2 12L12 17L22 12"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  <h2 className="text-white fw-bold mb-2" style={{ letterSpacing: '-0.02em' }}>
                    Pharmacy Management System
                  </h2>
                  <p className="text-white text-opacity-90 mb-0">Secure access to your dashboard</p>
                </div>

                <CardBody className="p-4 p-lg-5">
                  {/* STEP 1: Enter Email or Phone */}
                  {step === 'identifier' && (
                    <div>
                      <div className="text-center mb-4">
                        <h4 className="fw-semibold mb-2">Welcome back</h4>
                        <p className="text-muted">Sign in to continue to your account</p>
                      </div>

                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          identifierForm.handleSubmit();
                          return false;
                        }}
                      >
                        {checkError && (
                          <Alert color="danger" className="border-0 rounded-3">
                            <div className="d-flex align-items-center">
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="me-2"
                              >
                                <path
                                  d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              {getFormErrors(checkError) ||
                                'No accounts found with this identifier.'}
                            </div>
                          </Alert>
                        )}

                        <div className="mb-4">
                          <Label className="form-label fw-semibold text-secondary">
                            Email or Phone Number
                          </Label>
                          <Input
                            name="emailOrPhone"
                            className="form-control-lg border-0 bg-light rounded-3"
                            placeholder="Enter your email or phone number"
                            style={{ backgroundColor: '#F3F4F6' }}
                            onChange={identifierForm.handleChange}
                            onBlur={identifierForm.handleBlur}
                            value={identifierForm.values.emailOrPhone}
                            invalid={
                              identifierForm.touched.emailOrPhone &&
                              !!identifierForm.errors.emailOrPhone
                            }
                          />
                          {identifierForm.touched.emailOrPhone &&
                          identifierForm.errors.emailOrPhone ? (
                            <FormFeedback type="invalid" className="mt-1">
                              {identifierForm.errors.emailOrPhone}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <Button
                          color="primary"
                          type="submit"
                          disabled={isCheckingIdentifier}
                          className="w-100 btn-lg rounded-3 fw-semibold mb-3"
                          style={{
                            background: 'linear-gradient(120deg, #4F46E5 0%, #7C3AED 100%)',
                            border: 'none',
                            boxShadow: '0 4px 14px 0 rgba(79, 70, 229, 0.4)',
                          }}
                        >
                          {isCheckingIdentifier ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Checking...
                            </>
                          ) : (
                            'Continue'
                          )}
                        </Button>

                        <div className="text-center">
                          <Link to="/forgot-password" className="text-decoration-none small">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="me-1 d-inline-block"
                            >
                              <path
                                d="M12 15V17M6 21H18C19.1046 21 20 20.1046 20 19V13C20 11.8954 19.1046 11 18 11H6C4.89543 11 4 11.8954 4 13V19C4 20.1046 4.89543 21 6 21ZM16 11V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V11H16Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Forgot your password?
                          </Link>
                        </div>
                      </Form>
                    </div>
                  )}

                  {/* STEP 2: Select Tenant */}
                  {step === 'tenant-select' && (
                    <div>
                      <div className="text-center mb-4">
                        <div className="bg-info bg-opacity-10 rounded-circle d-inline-flex p-2 mb-3">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M19 21V19C19 16.2386 16.7614 14 14 14H10C7.23858 14 5 16.2386 5 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <h4 className="fw-semibold mb-2">Select Organization</h4>
                        <p className="text-muted small">
                          Multiple accounts found. Please select one
                        </p>
                      </div>

                      <div className="mb-4">
                        {availableTenants.map((tenant) => (
                          <button
                            key={tenant.tenantId}
                            onClick={() => handleTenantSelect(tenant.tenantId)}
                            className="w-100 text-start p-3 mb-3 border rounded-3 bg-white transition-all"
                            style={{
                              borderColor: '#E5E7EB',
                              borderRadius: '0.75rem',
                              transition: 'all 0.2s ease',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#4F46E5';
                              e.currentTarget.style.backgroundColor = '#F9FAFB';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#E5E7EB';
                              e.currentTarget.style.backgroundColor = 'white';
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M19 21V19C19 16.2386 16.7614 14 14 14H10C7.23858 14 5 16.2386 5 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
                                    stroke="#4F46E5"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="fw-semibold">{tenant.tenantName}</div>
                                <div className="text-muted small">{tenant.tenantId}</div>
                              </div>
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="ms-auto"
                              >
                                <path
                                  d="M9 18L15 12L9 6"
                                  stroke="#9CA3AF"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          </button>
                        ))}
                      </div>

                      <Button color="light" onClick={handleBack} className="w-100 btn-lg rounded-3">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="me-2 d-inline-block"
                        >
                          <path
                            d="M10 19L3 12M3 12L10 5M3 12H21"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Back
                      </Button>
                    </div>
                  )}

                  {/* STEP 3: Enter Password */}
                  {step === 'password' && (
                    <div>
                      <div className="text-center mb-4">
                        <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex p-2 mb-3">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 15V17M6 21H18C19.1046 21 20 20.1046 20 19V13C20 11.8954 19.1046 11 18 11H6C4.89543 11 4 11.8954 4 13V19C4 20.1046 4.89543 21 6 21ZM16 11V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V11H16Z"
                              stroke="#10B981"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <h4 className="fw-semibold mb-2">Enter your password</h4>
                        <p className="text-muted small">
                          Signing in as{' '}
                          <strong className="text-primary">
                            {availableTenants.find((t) => t.tenantId === selectedTenant)
                              ?.tenantName || identifier}
                          </strong>
                        </p>
                      </div>

                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          passwordForm.handleSubmit();
                          return false;
                        }}
                      >
                        {loginError && (
                          <Alert color="danger" className="border-0 rounded-3 mb-4">
                            <div className="d-flex align-items-center">
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="me-2"
                              >
                                <path
                                  d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              {getFormErrors(loginError) || 'Authentication failed.'}
                            </div>
                          </Alert>
                        )}

                        <div className="mb-4">
                          <Label className="form-label fw-semibold text-secondary">Password</Label>
                          <Input
                            name="password"
                            type="password"
                            className="form-control-lg border-0 bg-light rounded-3"
                            placeholder="Enter your password"
                            style={{ backgroundColor: '#F3F4F6' }}
                            onChange={passwordForm.handleChange}
                            onBlur={passwordForm.handleBlur}
                            value={passwordForm.values.password}
                            invalid={
                              passwordForm.touched.password && !!passwordForm.errors.password
                            }
                          />
                          {passwordForm.touched.password && passwordForm.errors.password ? (
                            <FormFeedback type="invalid" className="mt-1">
                              {passwordForm.errors.password}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <Button
                          color="primary"
                          type="submit"
                          disabled={isLoggingIn}
                          className="w-100 btn-lg rounded-3 fw-semibold mb-3"
                          style={{
                            background: 'linear-gradient(120deg, #4F46E5 0%, #7C3AED 100%)',
                            border: 'none',
                            boxShadow: '0 4px 14px 0 rgba(79, 70, 229, 0.4)',
                          }}
                        >
                          {isLoggingIn ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Signing in...
                            </>
                          ) : (
                            'Sign In'
                          )}
                        </Button>

                        <Button
                          color="light"
                          onClick={handleBack}
                          className="w-100 btn-lg rounded-3"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="me-2 d-inline-block"
                          >
                            <path
                              d="M10 19L3 12M3 12L10 5M3 12H21"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Back
                        </Button>

                        <div className="text-center mt-4">
                          <Link to="/forgot-password" className="text-decoration-none small">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="me-1 d-inline-block"
                            >
                              <path
                                d="M12 15V17M6 21H18C19.1046 21 20 20.1046 20 19V13C20 11.8954 19.1046 11 18 11H6C4.89543 11 4 11.8954 4 13V19C4 20.1046 4.89543 21 6 21ZM16 11V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V11H16Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Forgot your password?
                          </Link>
                        </div>
                      </Form>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Footer */}
              <div className="text-center mt-4">
                <p className="text-white-50 small mb-0">
                  © {new Date().getFullYear()} Pharmacy Management. All rights reserved.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TenantLogin;
